import React from 'react';
import './styles/PaymentPlanSchedules.scss';
import { Control } from 'react-redux-form';
import { ensureKeyed } from 'utils';
import PropTypes from 'prop-types';
import { SchoolYearType } from '../../../propTypes';

const startYear = (schoolYear) => parseInt(schoolYear.start_year, 10);
const prevYear = (schoolYear) => startYear(schoolYear) - 1;
const nextYear = (schoolYear) => startYear(schoolYear) + 1;

const ScheduleItemRenderer = (
  {
    paymentNumber,
    formScheduleItems,
    formNumOfPayments,
    databaseScheduleItems,
    schoolYear,
    numOfPayments,
    numEditable,
    overallPercent,
    itemsName
  }
) => {
  const firstEditableIndex = numOfPayments - numEditable;

  const calculateMonthAndYear = () => {
    const firstEditableItem = formScheduleItems && formScheduleItems[firstEditableIndex];

    const firstEditableMonth = firstEditableItem ? parseInt(firstEditableItem.month, 10) : 1;
    const firstEditableYear = firstEditableItem ? parseInt(firstEditableItem.year, 10) : startYear(schoolYear);

    // Handles auto incrementing the months when a new start month is selected
    const unModdedMonth = firstEditableMonth + (paymentNumber - firstEditableIndex);
    const calculatedMonth = unModdedMonth > 12 ? unModdedMonth - 12 : unModdedMonth;
    // We don't currently want to go beyond the last year when auto-cascading, so don't increment past nextYear
    const calculatedYear = unModdedMonth > 12 && firstEditableYear !== nextYear(schoolYear)
      ? firstEditableYear + 1
      : firstEditableYear;

    return { month: calculatedMonth, year: calculatedYear };
  };

  const calculatePercent = () => {
    const typicalPercent = Number(overallPercent / numEditable).toFixed(2);
    const isLastPercent = paymentNumber + 1 === numOfPayments;

    if (isLastPercent) {
      const diff = overallPercent - (parseFloat(typicalPercent) * parseFloat(numEditable));
      return (parseFloat(typicalPercent) + diff).toFixed(2);
    }
    return typicalPercent;
  };

  // Apparently number_of_payments is a string once changed so won't match numOfPayments, an int.
  const useDatabase = formNumOfPayments === numOfPayments;
  const databaseScheduleItem = useDatabase ? databaseScheduleItems[paymentNumber] : null;
  const modelKey = `.${itemsName}[${paymentNumber}]`;

  if (schoolYear.billing_enabled) {
    const editable = paymentNumber >= firstEditableIndex;

    let scheduleItem = databaseScheduleItem;
    if (!scheduleItem) {
      if (!editable) {
        scheduleItem = formScheduleItems[paymentNumber];
      } else {
        scheduleItem = {
          ...calculateMonthAndYear(),
          percent: calculatePercent()
        };
      }
    }

    return (
      <ScheduleItem
        paymentNumber={paymentNumber}
        item={scheduleItem}
        schoolYear={schoolYear}
        editable={editable}
        modelKey={modelKey}
      />
    );
  }

  return (
    <LegacyScheduleItem
      databaseScheduleItem={databaseScheduleItem}
      modelKey={modelKey}
    />
  );
};
ScheduleItemRenderer.propTypes = {
  paymentNumber: PropTypes.number.isRequired,
  formScheduleItems: PropTypes.arrayOf(PropTypes.shape({
    year: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    month: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    percent: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  })).isRequired,
  formNumOfPayments: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  databaseScheduleItems: PropTypes.arrayOf(PropTypes.shape({
    year: PropTypes.number,
    month: PropTypes.number,
    percent: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  })).isRequired,
  schoolYear: SchoolYearType.isRequired,
  numOfPayments: PropTypes.number.isRequired,
  numEditable: PropTypes.number.isRequired,
  overallPercent: PropTypes.number.isRequired,
  itemsName: PropTypes.string.isRequired
};
ScheduleItemRenderer.defaultProps = {
  formNumOfPayments: null
};
const MONTHS = {
  January: 1,
  February: 2,
  March: 3,
  April: 4,
  May: 5,
  June: 6,
  July: 7,
  August: 8,
  September: 9,
  October: 10,
  November: 11,
  December: 12
};

const LegacyScheduleItem = ({ databaseScheduleItem, modelKey }) => {
  const useDatabaseValue = !!databaseScheduleItem;

  return (
    <div>
      <div className='payment-plan-schedule-field'>
        <label className='col-form-label'>Due Date</label>
        <Control.text
          className='form-control form-control-sm payment-plan-schedule-input'
          model={`${modelKey}.due_date_display`}
          defaultValue={useDatabaseValue ? databaseScheduleItem.due_date_display : ''}
        />
      </div>
      <div className='payment-plan-schedule-field'>
        <label className='col-form-label'>Percent</label>
        <Control.text
          className='form-control form-control-sm payment-plan-schedule-input'
          model={`${modelKey}.percent`}
          defaultValue={useDatabaseValue ? databaseScheduleItem.percent : 0}
        />
      </div>
      <Control.text className='hide-schedule-field' disabled model={`${modelKey}._id`} />
    </div>
  );
};
LegacyScheduleItem.propTypes = {
  databaseScheduleItem: PropTypes.oneOfType([PropTypes.shape({
    year: PropTypes.number,
    month: PropTypes.number,
    percent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    due_date_display: PropTypes.string
  }), PropTypes.any]),
  modelKey: PropTypes.string.isRequired
};
LegacyScheduleItem.defaultProps = {
  databaseScheduleItem: null
};

const ScheduleItem = (
  {
    paymentNumber,
    item,
    schoolYear,
    editable,
    modelKey
  }
) => {
  // this div use a unique key so we keep updating the default values if the start month or number of payments changes
  const key = `${item.month}-${item.percent}-${item.year}`;
  return (
    <div key={key}>
      <div className='payment-plan-schedule-field'>
        <label className='col-form-label'>{paymentNumber === 0 ? 'Start Month' : 'Month'}</label>
        <Control.select
          className='form-select form-select-sm payment-plan-schedule-input'
          model={`${modelKey}.month`}
          disabled={!editable}
          defaultValue={item.month}
        >
          {Object.keys(MONTHS).map((month) => <option value={MONTHS[month]}>{month}</option>).map(ensureKeyed())}
        </Control.select>
      </div>
      <div className='payment-plan-schedule-field'>
        <label className='col-form-label'>Year</label>
        <Control.select
          className='form-select form-select-sm payment-plan-schedule-input'
          model={`${modelKey}.year`}
          disabled={!editable}
          defaultValue={item.year}
        >
          <option value={prevYear(schoolYear)}>{prevYear(schoolYear)}</option>
          <option value={startYear(schoolYear)}>{startYear(schoolYear)}</option>
          <option value={nextYear(schoolYear)}>{nextYear(schoolYear)}</option>
        </Control.select>
      </div>
      <div className='payment-plan-schedule-field'>
        <label className='col-form-label'>Percent</label>
        <Control.text
          className='form-control form-control-sm payment-plan-schedule-input'
          model={`${modelKey}.percent`}
          disabled={!editable}
          defaultValue={item.percent}
        />
      </div>
      <Control.text className='hide-schedule-field' disabled model={`${modelKey}._id`} />
    </div>
  );
};
ScheduleItem.propTypes = {
  paymentNumber: PropTypes.number.isRequired,
  item: PropTypes.oneOfType([PropTypes.shape({
    year: PropTypes.number,
    month: PropTypes.number,
    percent: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }), PropTypes.any]),
  schoolYear: SchoolYearType.isRequired,
  editable: PropTypes.bool.isRequired,
  modelKey: PropTypes.string.isRequired
};
ScheduleItem.defaultProps = {
  item: null
};

export default ScheduleItemRenderer;
